/**
 * `about_block` — the seller's story (spec 42 R2; spec 28 R4).
 *
 * Clamped to two lines with a "Read more" toggle. The **full text is always in
 * the DOM** — clamping is CSS, not truncation — so crawlers and screen readers
 * get the whole thing from the server-rendered HTML regardless of the toggle.
 */
import { useState } from "react";

export function AboutBlock({
  storeName,
  about,
  heading,
  body,
}: {
  storeName: string;
  about: string | null;
  /** Seller-authored title; falls back to "About {storeName}". */
  heading?: string | undefined;
  /** Seller-authored copy; falls back to the store profile's About text. */
  body?: string | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  const text = body || about;
  if (!text) return null;
  const title = heading || `About ${storeName}`;

  return (
    <section
      aria-label={title}
      style={{ paddingBlock: "0.5rem", textAlign: "center", maxWidth: "760px", marginInline: "auto" }}
    >
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 600,
          fontSize: "clamp(1.5rem, 3.4vw, 2rem)",
          color: "var(--ink)",
          marginBottom: "0.75rem",
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontSize: "1.0625rem",
          lineHeight: 1.7,
          color: "var(--muted)",
          maxWidth: "62ch",
          marginInline: "auto",
          ...(expanded
            ? {}
            : {
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical" as const,
                overflow: "hidden",
              }),
        }}
      >
        {text}
      </p>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        style={{
          marginTop: "0.25rem",
          padding: 0,
          border: "none",
          background: "none",
          color: "var(--accent)",
          fontSize: "0.8125rem",
          cursor: "pointer",
        }}
      >
        {expanded ? "Show less" : "Read more"}
      </button>
    </section>
  );
}
